import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import translations from '../data/i18n';

const AppContext = createContext(null);

const THEME_KEY = 'horus.theme';      // 'system' | 'light' | 'dark'
const LANG_KEY  = 'horus.lang';       // 'id' | 'en'

const readStored = (key, valid, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v && valid.includes(v) ? v : fallback;
  } catch { return fallback; }
};

// Theme dari jam sistem:
// 06:00–17:59 = light (siang)
// 18:00–05:59 = dark (malam)
function themeFromClock(hour = new Date().getHours()) {
  return (hour >= 6 && hour < 18) ? 'light' : 'dark';
}

// Theme dari OS prefers-color-scheme media query.
// Returns 'light' | 'dark' | null (kalau browser tidak support).
function themeFromOS() {
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return null;
}

// Resolver mode 'system' → effective theme.
// Prioritas: OS prefers-color-scheme > clock-based fallback.
function resolveSystemTheme() {
  return themeFromOS() || themeFromClock();
}

export const AppProvider = ({ children }) => {
  // Theme MODE: 'system' (default) | 'light' | 'dark'.
  // Default 'system' = follow OS / fallback ke jam sistem.
  // User toggle cycle: system → light → dark → system.
  const [themeMode, setThemeMode] = useState(() => readStored(THEME_KEY, ['system', 'light', 'dark'], 'system'));
  const [lang, setLang] = useState(() => readStored(LANG_KEY, ['id', 'en'], 'id'));
  // Tick state untuk re-evaluate effective theme (re-check OS pref + clock periodically)
  const [systemTick, setSystemTick] = useState(0);

  // Effective theme: kalau mode 'system', resolve via OS/clock; else pakai mode-nya.
  const effectiveTheme = useMemo(() => {
    if (themeMode === 'system') return resolveSystemTheme();
    return themeMode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMode, systemTick]);

  // Apply data-theme attribute setiap effective theme berubah
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveTheme);
  }, [effectiveTheme]);

  // Persist mode + lang
  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, themeMode); } catch { /* quota */ }
  }, [themeMode]);

  useEffect(() => {
    try { localStorage.setItem(LANG_KEY, lang); } catch { /* quota */ }
  }, [lang]);

  // Saat mode 'system': listen OS prefers-color-scheme changes + clock tick setiap menit
  useEffect(() => {
    if (themeMode !== 'system') return;
    let mq = null;
    const onMqChange = () => setSystemTick(t => t + 1);
    if (typeof window !== 'undefined' && window.matchMedia) {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener?.('change', onMqChange);
    }
    const intv = setInterval(() => setSystemTick(t => t + 1), 60_000);
    return () => {
      mq?.removeEventListener?.('change', onMqChange);
      clearInterval(intv);
    };
  }, [themeMode]);

  const t = (key) => translations[lang][key] ?? key;

  // Toggle cycle: system → light → dark → system
  const toggleTheme = () => setThemeMode(prev => {
    if (prev === 'system') return 'light';
    if (prev === 'light')  return 'dark';
    return 'system';
  });
  const toggleLang = () => setLang(prev => prev === 'id' ? 'en' : 'id');

  const value = {
    theme: effectiveTheme,         // 'light' | 'dark' (resolved, untuk komponen yang butuh actual theme)
    themeMode,                     // 'system' | 'light' | 'dark' (untuk Header toggle button)
    setThemeMode,
    toggleTheme,
    lang,
    toggleLang,
    t,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
